import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import { useResourceList } from '../config/ResourcesConfigContext';
import type { ContentAudience } from '../utils/audience';

export interface VideoItemData {
    id: number;
    title: string;
    slug: string;
    description: string;
    videoUrl: string;
    category: string[];
    createdAt: string;
    audience: ContentAudience;
}

interface VideosContextType {
    videos: VideoItemData[];
    isLoading: boolean;
    error: string | null;
    refresh: (force?: boolean) => Promise<void>;
    reset: () => void;
}

const VideosContext = createContext<VideosContextType | undefined>(undefined);

export function VideosProvider({ children }: { children: ReactNode }) {
    const fetchResource = useResourceList();
    const [videos, setVideos] = useState<VideoItemData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);

    const fetchVideos = useCallback(
        async (force: boolean = false) => {
            if (!force && hasFetchedRef.current) {
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const data = await fetchResource<VideoItemData>('videos');
                setVideos(data);
                hasFetchedRef.current = true;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to fetch videos';
                setError(errorMessage);
                console.error('Failed to fetch videos:', err);
            } finally {
                setIsLoading(false);
            }
        },
        [fetchResource]
    );

    const refresh = useCallback(
        async (force: boolean = false) => {
            await fetchVideos(force);
        },
        [fetchVideos]
    );
    const reset = useCallback(() => {
        hasFetchedRef.current = false;
        setVideos([]);
        setError(null);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchVideos(false);
    }, [fetchVideos]);

    const value: VideosContextType = { videos, isLoading, error, refresh, reset };

    return <VideosContext.Provider value={value}>{children}</VideosContext.Provider>;
}

export function useVideos(): VideosContextType {
    const context = useContext(VideosContext);
    if (context === undefined) {
        throw new Error('useVideos must be used within a VideosProvider');
    }
    return context;
}
